import { queryOptions } from "@tanstack/react-query"
import { api } from "./api"
import { listAllData } from "./list-all"
import type { RichTextDoc } from "./rich-text"
import type { OutgoingAttachment } from "./attachments"

// Channels data layer — channels are `conversations` rows with scope='channel'.
// Mirrors the admin SPA's ChannelsPage endpoints. CRITICAL: the query keys here
// match admin/tickets byte-for-byte (`["resource", <table>, <filters>]`) so the
// vendored realtime layer (realtime.ts → cache.ts) surgically patches these
// caches on live CRUD events with no extra wiring:
//   • conversations list   → ["resource", "conversations", { eventId, scope }]
//   • a channel's messages → ["resource", "chat-messages", { conversationId }]
// chat-messages INSERT frames are spliced into the open thread (DESC → prepend)
// by tryMessageInsertSplice; conversation UPDATE/DELETE frames patch the list.

export type Conversation = {
  id: string
  eventId: string
  scope: string
  name: string | null
  topic: string | null
  emoji: string | null
  agendaItemId: string | null
  agendaGroupId: string | null
  attendeeGroupId: string | null
  cohortKind?: string | null
  cohortKey?: string | null
  includesAllConfirmedGuests: number
  postingMode: string
  // Organizer-set default notify level for members without an override.
  // Absent ⇒ "all". Updatable via PATCH (member+).
  defaultNotifyLevel?: NotifyLevel | null
  // Who may use @everyone / @here / @all. Absent ⇒ "members". PATCH (member+).
  everyoneMentionPolicy?: EveryoneMentionPolicy | null
  // Internal channels only: "all" (every staffer) | "selected" (explicit roster).
  // null/absent == "all" for back-compat.
  staffAudience?: string | null
  pinnedAtTop: number
  archivedAt: string | null
  createdAt: string
  modifiedAt: string | null
  lastMessage?: ConversationLastMessage | null
  unread?: number
  // True when an unread message in this conversation @-mentions me (server-
  // computed by list-my-conversations). Drives the personal "@Me" indicator.
  mentionsMe?: boolean
  canPost?: boolean
  // My read pointer — snapshot on open for the "New" divider.
  myRead?: { lastReadAt: string; lastReadMessageId: string | null } | null
}

export type ConversationLastMessage = {
  id: string
  authorName: string | null
  authorRole: string | null
  body: string | null
  kind: string
  createdAt: string
}

export type ChatMessage = {
  id: string
  conversationId: string
  // NULL when a sessionless attendee authored the message (guest path) — the
  // server then sets `authorGuestId` instead. `authorNameAtSend` is always set.
  authorUserId: string | null
  authorGuestId?: string | null
  authorNameAtSend: string
  authorRoleAtSend: string | null
  body: string | null
  // Constrained Tiptap doc (JSON string) derived server-side. Null for legacy /
  // trivial messages — RichText falls back to `body`.
  richText?: string | null
  // JSON array of attachments (file/image/document), stored verbatim + returned on read.
  // Null when none. Parsed by parseMessageAttachments.
  attachments?: string | null
  kind: string
  urgency?: "normal" | "urgent" | string | null
  createdAt: string
  // Edit/delete lifecycle — server columns on chat_messages. editedAt stamped
  // by editMessage; deletedAt by deleteMessage (soft delete). Null when neither.
  editedAt?: string | null
  deletedAt?: string | null
  // JSON array of resolved @-mentions; raw JSON, parsed downstream. Null = none.
  mentions?: string | null
  // JSON array of link unfurl cards; written async (waitUntil) post-send and
  // re-broadcast as an `edited` frame. Raw JSON, rendered by LinkUnfurl. Null = none.
  unfurls?: string | null
  // Threading — server columns on chat_messages. parentMessageId set on replies;
  // replyCount/lastReplyAt bumped on the root. Null on root rows with no replies.
  parentMessageId?: string | null
  replyCount?: number | null
  lastReplyAt?: string | null
  // Back-reference for a thread reply that was ALSO posted to the main stream
  // ("Also post"): the thread root id + a short preview of the root, used to
  // render the "Replied to a thread" header on the reflected copy. NOTE: backend
  // does not persist/return these yet (chat_messages has no column) — they are
  // currently a client-side carry stamped on the reflected copy. See post-message
  // gap flagged in the fork report. Distinct from parentMessageId (which threads).
  reflectedFromThreadRootId?: string | null
  reflectedFromThreadPreview?: string | null
  // Derived on kind="poll" rows (quickback/features/chat/lib/poll.ts PollView).
  poll?: PollView | null
  // Derived on kind="qa"|"person" rows (quickback/features/chat/lib/poll.ts CardView).
  card?: CardView | null
  // Client-only optimistic placeholder; cleared when the server echo reconciles.
  pending?: boolean
  clientId?: string | null
}

export type PollView = {
  question: string
  options: { id: string; label: string }[]
  allowMultiple: boolean
  resultsVisibility: "afterVote" | "afterClose"
  // false ⇒ a cast vote is final (vote-poll → 409 POLL_VOTE_LOCKED). Absent ⇒ true.
  allowVoteChange?: boolean
  closedAt: string | null
  // null when hidden from this caller (afterClose, still open) — and on every
  // `poll` frame for afterClose polls; staff refetch getPollResults then.
  counts: { optionId: string; count: number }[] | null
  totalVoters: number
  // Caller's picked option ids. Always [] on broadcast frames — keep our own.
  mine: string[]
}

export type PollInput = {
  question: string
  options: string[]
  allowMultiple: boolean
  resultsVisibility: "afterVote" | "afterClose"
  allowVoteChange: boolean
}

// Q&A / person cards (kind="qa"|"person"). Q&A questions are THREAD REPLIES to
// the card (count = root replyCount); upvotes = the reserved QA_UPVOTE reaction
// on a reply. Person cards are never closed (closedAt always null).
export type QaCardView = {
  kind: "qa"
  prompt: string
  title: string
  icon: string
  placeholder: string
  submitLabel: string
  itemLabel: { one: string; other: string }
  closedAt: string | null
}
export type PersonCardView = {
  kind: "person"
  name: string
  title: string | null
  company: string | null
  bio: string | null
  links: { label: string; url: string }[]
  closedAt: null
}
export type CardView = QaCardView | PersonCardView

export const QA_UPVOTE = "▲"

// Unset optional fields fall back to the server's Q&A defaults.
export type QaInput = {
  prompt: string
  title?: string
  icon?: string
  placeholder?: string
  submitLabel?: string
  itemLabelOne?: string
  itemLabelOther?: string
}

export type PersonInput = {
  name: string
  title?: string
  company?: string
  bio?: string
  links?: { label: string; url: string }[]
}

export type ConversationMember = {
  id: string
  conversationId: string
  userId: string
  role: string | null
}

export type AttendeeGroupLite = {
  id: string
  name: string
  visibility: string
  isAutomatic: boolean
  memberCount: number
}

type ConversationInboxEnvelope = { success: true; conversations: ConversationInboxRow[] }
type ConversationInboxRow = Omit<
  Conversation,
  "includesAllConfirmedGuests" | "pinnedAtTop" | "attendeeGroupId"
> & {
  attendeeGroupId?: string | null
  includesAllConfirmedGuests: boolean | number
  pinnedAtTop: boolean | number
  lastMessage?: ConversationLastMessage | null
  unread?: number
  canPost?: boolean
}

// ── query keys (must match the realtime splice predicates) ───────────────────
export const channelsKey = (eventId: string) =>
  ["resource", "conversations", { eventId, scope: "channel" }] as const
export const channelMessagesKey = (conversationId: string) =>
  ["resource", "chat-messages", { conversationId }] as const
export const channelMembersKey = (conversationId: string) =>
  ["resource", "conversation-members", { conversationId }] as const

// ── query options ────────────────────────────────────────────────────────────
export function channelsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: channelsKey(eventId),
    queryFn: () =>
      api<ConversationInboxEnvelope>(`/api/v1/event/${eventId}/chat/list-my-conversations`, {
        method: "POST",
        body: {},
      }),
    select: (r): Conversation[] =>
      r.conversations
        .filter((c) => c.scope === "channel")
        .map((c) => ({
          ...c,
          attendeeGroupId: c.attendeeGroupId ?? null,
          includesAllConfirmedGuests: c.includesAllConfirmedGuests ? 1 : 0,
          pinnedAtTop: c.pinnedAtTop ? 1 : 0,
          createdAt: c.createdAt ?? c.lastMessage?.createdAt ?? "",
          modifiedAt: c.modifiedAt ?? c.lastMessage?.createdAt ?? c.createdAt ?? null,
          unread: c.unread ?? 0,
          lastMessage: c.lastMessage ?? null,
        })),
  })
}

// NOTE: channel messages are fetched via the same `list-conversation-messages`
// action as DMs (channels are conversations with scope='channel'; the action
// gates by membership, which always admits staff). ChannelThread imports
// conversationMessagesQueryOptions / fetchConversationMessagesPage directly so
// channels get the bundled reactions + cursor pagination for free. The old raw
// /chat-messages CRUD fetch (offset-based, no reactions) is retired.

export function channelMembersQueryOptions(conversationId: string) {
  return queryOptions({
    queryKey: channelMembersKey(conversationId),
    queryFn: () =>
      listAllData<ConversationMember>("/api/v1/conversation-members", { conversationId }),
    select: (r) => r.data,
  })
}

// ── posting mode (matches conversations.postingMode) ─────────────────────────
// Stored values are the same regardless of audience ("members" | "organizers-only");
// only the LABELS + framing differ. Guest-facing channels frame the open mode as
// "Everyone" and the locked mode as "read-only for guests"; internal/staff
// channels have no guests, so they read "Members" / "Organizers only".
export type PostingMode = "members" | "organizers-only"
export type PostingModeOption = { value: PostingMode; label: string }

export function postingModesFor(guestFacing: boolean): PostingModeOption[] {
  return guestFacing
    ? [
        { value: "members", label: "Everyone — anyone can post" },
        { value: "organizers-only", label: "Organizers only — read-only for guests" },
      ]
    : [
        { value: "members", label: "Members — anyone in the channel can post" },
        { value: "organizers-only", label: "Organizers only" },
      ]
}

// ── notify levels (Slack-style All / Mentions / Team / Nothing) ──────────────
// Effective level = my override ?? the conversation's defaultNotifyLevel ?? "all".
// "team" = only messages posted by the event team. Announcements
// (organizers-only) + agenda-required channels reject "nothing"
// (403 CHANNEL_NOT_MUTABLE). Order is loud → quiet.
export type NotifyLevel = "all" | "mentions" | "team" | "nothing"
export const NOTIFY_LEVELS: { value: NotifyLevel; label: string }[] = [
  { value: "all", label: "All messages" },
  { value: "mentions", label: "Mentions only" },
  { value: "team", label: "Event team only" },
  { value: "nothing", label: "Nothing" },
]

// Who can use @everyone / @here / @all in a channel.
export type EveryoneMentionPolicy = "team" | "members"
export const EVERYONE_MENTION_POLICIES: { value: EveryoneMentionPolicy; label: string }[] = [
  { value: "team", label: "Event team only" },
  { value: "members", label: "Everyone in the channel" },
]
export const EVERYONE_MENTION_HINT = "@everyone, @here and @all notify everyone in the channel"

export const notifyLevelsKey = (eventId: string) => ["notify-levels", eventId] as const

export function notifyLevelsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: notifyLevelsKey(eventId),
    queryFn: () =>
      api<{ items: { conversationId: string; level: NotifyLevel }[] }>(
        `/api/v1/event/${eventId}/notifications/channel-levels`,
      ),
    select: (r) => new Map(r.items.map((i) => [i.conversationId, i.level])),
  })
}

export async function setChannelNotifyLevel(eventId: string, conversationId: string, level: NotifyLevel) {
  return api<{ success: true; conversationId: string; level: NotifyLevel }>(
    `/api/v1/event/${eventId}/notifications/channel-level`,
    { method: "POST", body: { conversationId, level } },
  )
}

// ── mutations ────────────────────────────────────────────────────────────────
export type PostChannelMessageInput = {
  conversationId: string
  eventId: string
  body: string
  urgency: "normal" | "urgent"
  clientId: string
  richTextDoc?: RichTextDoc
  // Uploaded file attachments (presigned-R2 mediaIds) and/or shared library
  // documents ({ kind:"document", documentId }) and links ({ kind:"link",
  // linkId } | { kind:"link", url, title? }). Empty/omitted = none. A
  // reference-only message may send an empty body (server stamps "📄 <title>"
  // / "🔗 <title>").
  attachments?: OutgoingAttachment[]
  // Set on a thread REPLY — the server links it under this root and bumps the
  // root's replyCount/lastReplyAt. Omitted = a top-level message.
  parentMessageId?: string
  // Set on a top-level "Also post" copy of a thread reply: the thread root id
  // this copy reflects (NOT parentMessageId — the copy must stay top-level so it
  // shows in the main stream). Forwarded so the backend can persist it once a
  // column lands; ignored server-side today. Drives the "Replied to a thread" row.
  reflectedFromThreadRootId?: string
  // Post a poll card instead of text (staff, channel root only). Body ignored.
  poll?: PollInput
  // …or a Q&A / person card (same rules as polls).
  qa?: QaInput
  person?: PersonInput
}

export async function postChannelMessage(
  input: PostChannelMessageInput,
): Promise<{ message: ChatMessage; memberCount?: number }> {
  return api<{ message: ChatMessage; memberCount?: number }>(
    `/api/v1/event/${input.eventId}/chat/post-message`,
    {
      method: "POST",
      body: {
        conversationId: input.conversationId,
        body: input.body,
        urgency: input.urgency,
        clientId: input.clientId,
        ...(input.richTextDoc ? { richTextDoc: input.richTextDoc } : {}),
        ...(input.attachments && input.attachments.length > 0
          ? { attachments: input.attachments }
          : {}),
        ...(input.parentMessageId ? { parentMessageId: input.parentMessageId } : {}),
        ...(input.reflectedFromThreadRootId
          ? { reflectedFromThreadRootId: input.reflectedFromThreadRootId }
          : {}),
        ...(input.poll ? { poll: input.poll } : {}),
        ...(input.qa ? { qa: input.qa } : {}),
        ...(input.person ? { person: input.person } : {}),
        dryRun: false,
      },
    },
  )
}

// ── polls ────────────────────────────────────────────────────────────────────
// Vote REPLACES the caller's whole selection ([] clears). 409 POLL_CLOSED once
// closed. close-card (polls + Q&A cards) reopens with closed:false.
export async function votePoll(eventId: string, messageId: string, optionIds: string[]) {
  return api<{ success: true; messageId: string; poll: PollView }>(
    `/api/v1/event/${eventId}/chat/vote-poll`,
    { method: "POST", body: { messageId, optionIds } },
  )
}

export async function closeCard(eventId: string, messageId: string, closed: boolean) {
  return api<{ success: true; committed: boolean; messageId: string; closedAt: string | null }>(
    `/api/v1/event/${eventId}/chat/close-card`,
    { method: "POST", body: { messageId, closed } },
  )
}

export type PollVoters = { optionId: string; voters: { name: string; userId: string | null; guestId: string | null }[] }[]

export async function getPollResults(eventId: string, messageId: string) {
  return api<{ success: true; messageId: string; poll: PollView; voters: PollVoters | null }>(
    `/api/v1/event/${eventId}/chat/get-poll-results`,
    { method: "POST", body: { messageId } },
  )
}

// Merge a poll update into every cached copy of its message. `keepMine` = the
// update came off a broadcast frame (mine is always [] there), so keep the
// cached one.
export function patchPollInCaches(
  qc: import("@tanstack/react-query").QueryClient,
  messageId: string,
  poll: PollView,
  keepMine: boolean,
): void {
  patchMessageInCaches(qc, messageId, (row) => ({
    ...row,
    poll: { ...poll, mine: keepMine ? (row.poll?.mine ?? []) : poll.mine },
  }))
}

export function patchCardInCaches(
  qc: import("@tanstack/react-query").QueryClient,
  messageId: string,
  card: CardView,
): void {
  patchMessageInCaches(qc, messageId, (row) => ({ ...row, card }))
}

// Apply `merge` to every cached copy of a message: channel/DM streams and an
// open thread's root.
export function patchMessageInCaches(
  qc: import("@tanstack/react-query").QueryClient,
  messageId: string,
  merge: (row: ChatMessage) => ChatMessage,
): void {
  const patchList = (rows: ChatMessage[]) => {
    const i = rows.findIndex((r) => r.id === messageId)
    if (i < 0) return rows
    const next = rows.slice()
    next[i] = merge(rows[i])
    return next
  }
  qc.setQueriesData(
    {
      predicate: (q) =>
        (q.queryKey[0] === "resource" && q.queryKey[1] === "chat-messages") ||
        q.queryKey[0] === "chat-thread",
    },
    (prev: unknown) => {
      if (Array.isArray(prev)) return patchList(prev as ChatMessage[])
      if (!prev || typeof prev !== "object") return prev
      const env = prev as { data?: ChatMessage[]; root?: ChatMessage | null }
      let next = env
      if (Array.isArray(env.data)) {
        const data = patchList(env.data)
        if (data !== env.data) next = { ...next, data }
      }
      if (env.root?.id === messageId) next = { ...next, root: merge(env.root) }
      return next
    },
  )
}

// Q&A thread order. Top = most ▲ first, ties oldest first; New = newest first.
export function sortQaReplies<T extends { id: string; createdAt: string }>(
  replies: T[],
  upvotes: (id: string) => number,
  mode: "top" | "new",
): T[] {
  const byAge = (a: T, b: T) => a.createdAt.localeCompare(b.createdAt)
  return replies
    .slice()
    .sort(mode === "new" ? (a, b) => byAge(b, a) : (a, b) => upvotes(b.id) - upvotes(a.id) || byAge(a, b))
}

export type CreateChannelInput = {
  eventId: string
  name: string | null
  topic: string | null
  emoji: string | null
  postingMode: string
  // Members' default notify level (omitted ⇒ the event's defaultChannelNotifyLevel).
  defaultNotifyLevel?: NotifyLevel
  // Omitted ⇒ the event's defaultEveryoneMentionPolicy.
  everyoneMentionPolicy?: EveryoneMentionPolicy
  // Membership source — exactly one of these resolves how attendees join.
  includesAllConfirmedGuests: boolean
  attendeeGroupId: string | null
  agendaItemId: string | null
  agendaGroupId: string | null
  // Travel-cohort attachment — set BOTH together (flight or transport). See
  // suggest{Flight,Transport}Cohorts for the cohortKey shapes.
  cohortKind?: CohortKind | null
  cohortKey?: string | null
  // Internal channels: "all" staff or a "selected" roster (memberUserIds).
  staffAudience?: "all" | "selected"
  memberUserIds?: string[]
}

export type CohortKind =
  | "flight_arrival"
  | "flight_departure"
  | "arrival_window"
  | "departure_window"
  | "ground_transfer"
  | "transfer_window"

export async function createChannel(input: CreateChannelInput): Promise<{ id: string }> {
  // Dedicated action (NOT raw CRUD): it seeds conversation_members for
  // select-staff channels. eventId is in the path; booleans, not 0/1.
  const r = await api<{ committed: boolean; id: string }>(
    `/api/v1/event/${input.eventId}/chat/create-channel`,
    {
      method: "POST",
      body: {
        name: input.name ?? "",
        topic: input.topic ?? undefined,
        emoji: input.emoji ?? undefined,
        postingMode: input.postingMode,
        defaultNotifyLevel: input.defaultNotifyLevel,
        everyoneMentionPolicy: input.everyoneMentionPolicy,
        agendaItemId: input.agendaItemId ?? undefined,
        agendaGroupId: input.agendaGroupId ?? undefined,
        attendeeGroupId: input.attendeeGroupId ?? undefined,
        includesAllConfirmedGuests: input.includesAllConfirmedGuests,
        cohortKind: input.cohortKind ?? undefined,
        cohortKey: input.cohortKey ?? undefined,
        staffAudience: input.staffAudience ?? "all",
        memberUserIds: input.memberUserIds ?? [],
      },
    },
  )
  return { id: r.id }
}

// ── internal-channel staff roster ────────────────────────────────────────────

export type EventStaff = {
  userId: string
  name: string | null
  email: string | null
  role: string
  // Null = external crew (event_roles only, no org seat).
  orgRole?: string | null
}

export async function listEventStaff(eventId: string): Promise<EventStaff[]> {
  const r = await api<{ staff?: EventStaff[] }>(`/api/v1/event/${eventId}/list-staff`, {
    method: "POST",
    body: {},
  })
  return r.staff ?? []
}

// Replace the explicit staff roster of a selected-staff internal channel.
export async function setChannelMembers(
  conversationId: string,
  userIds: string[],
): Promise<{ committed?: boolean; members?: Array<{ userId: string; role: string }> }> {
  return api(`/api/v1/conversations/${conversationId}/set-members`, {
    method: "POST",
    body: { userIds },
  })
}

// Settings save — name / topic / emoji / postingMode / pin go through a raw
// PATCH. archivedAt is guard-protected → archiveConversation action only.
export async function patchChannel(
  conversationId: string,
  patch: Partial<Conversation>,
): Promise<Record<string, unknown>> {
  return api<Record<string, unknown>>(`/api/v1/conversations/${conversationId}`, {
    method: "PATCH",
    body: patch,
  })
}

export async function archiveChannel(
  eventId: string,
  conversationId: string,
  archived: boolean,
): Promise<{ conversation?: Record<string, unknown> }> {
  return api<{ conversation?: Record<string, unknown> }>(
    `/api/v1/event/${eventId}/chat/archive-conversation`,
    { method: "POST", body: { conversationId, archived } },
  )
}

// Drop a channel's unread badge in the list cache without a refetch (on open).
export function clearChannelUnread(
  qc: import("@tanstack/react-query").QueryClient,
  eventId: string,
  conversationId: string,
): void {
  qc.setQueryData<ConversationInboxEnvelope>(channelsKey(eventId), (old) => {
    if (!old) return old
    let patched = false
    const next = old.conversations.map((c) => {
      if (c.id !== conversationId || !(c.unread || c.mentionsMe)) return c
      patched = true
      return { ...c, unread: 0, mentionsMe: false }
    })
    return patched ? { ...old, conversations: next } : old
  })
}

export async function loadAttendeeGroups(eventId: string): Promise<AttendeeGroupLite[]> {
  const r = await api<{ groups?: AttendeeGroupLite[] }>(
    `/api/v1/event/${eventId}/attendee-fields/setup`,
    { method: "POST", body: {} },
  )
  return r.groups ?? []
}

// ── travel-cohort channel suggestions ────────────────────────────────────────
// Powers the New Channel "Flight / Transport" picker. Each suggestion carries a
// (cohortKind, cohortKey) pair the dialog stamps straight onto createChannel.

export type FlightCohortSuggestion = {
  kind: "flight_arrival" | "flight_departure"
  cohortKey: string
  carrierIataCode: string
  flightNumber: string
  airportCode: string
  date: string
  sampleLocalAt: string
  count: number
}
export type FlightWindowSuggestion = {
  kind: "arrival_window" | "departure_window"
  cohortKey: string
  label: string
  airportCode: string
  date: string
  startTime: string
  endTime: string
  count: number
}
export type TransferCohortSuggestion = {
  kind: "ground_transfer"
  cohortKey: string
  mode: string
  direction: string
  vendorName: string
  pickupAt: string
  pickupLocationName: string | null
  count: number
}
export type TransferWindowSuggestion = {
  kind: "transfer_window"
  cohortKey: string
  label: string
  direction: string
  date: string
  startTime: string
  endTime: string
  count: number
}

export type AnyCohortSuggestion =
  | FlightCohortSuggestion
  | FlightWindowSuggestion
  | TransferCohortSuggestion
  | TransferWindowSuggestion

export function flightCohortsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["chat", "flight-cohorts", eventId] as const,
    queryFn: () =>
      api<{ flightCohorts: FlightCohortSuggestion[]; windowSuggestions: FlightWindowSuggestion[] }>(
        `/api/v1/event/${eventId}/chat/suggest-flight-cohorts`,
        { method: "POST", body: { minCount: 2 } },
      ),
  })
}

export function transportCohortsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["chat", "transport-cohorts", eventId] as const,
    queryFn: () =>
      api<{ transferCohorts: TransferCohortSuggestion[]; windowSuggestions: TransferWindowSuggestion[] }>(
        `/api/v1/event/${eventId}/chat/suggest-transport-cohorts`,
        { method: "POST", body: { minCount: 2 } },
      ),
  })
}

// HH:MM out of a naive `YYYY-MM-DDTHH:MM[:SS]` wall-clock string.
function cohortTimeLabel(localAt: string): string {
  const hhmm = localAt.slice(11, 16)
  if (!hhmm) return ""
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10))
  if (Number.isNaN(h)) return hhmm
  const ampm = h < 12 ? "AM" : "PM"
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`
}

// Human label + suggested channel name for each suggestion kind. Returns
// { title, name } — title for the pick-list row, name to prefill the field.
export function describeCohortSuggestion(
  s: FlightCohortSuggestion | FlightWindowSuggestion | TransferCohortSuggestion | TransferWindowSuggestion,
): { title: string; name: string } {
  switch (s.kind) {
    case "flight_arrival":
    case "flight_departure": {
      const verb = s.kind === "flight_arrival" ? "arriving" : "departing"
      const flight = `${s.carrierIataCode}${s.flightNumber}`
      return {
        title: `${flight} · ${verb} ${s.airportCode} · ${s.date} ${cohortTimeLabel(s.sampleLocalAt)}`,
        name: `${flight} ${s.airportCode}`,
      }
    }
    case "arrival_window":
    case "departure_window": {
      const verb = s.kind === "arrival_window" ? "Arrivals" : "Departures"
      return {
        title: `${verb} · ${s.airportCode} · ${s.date} · ${s.label} (${s.startTime}–${s.endTime})`,
        name: `${s.airportCode} ${s.label} ${verb}`,
      }
    }
    case "ground_transfer": {
      const mode = s.mode.replace(/_/g, " ")
      return {
        title: `${s.vendorName} · ${mode} · ${s.direction} · ${cohortTimeLabel(s.pickupAt)}${
          s.pickupLocationName ? ` · ${s.pickupLocationName}` : ""
        }`,
        name: `${s.vendorName} ${mode}`.trim(),
      }
    }
    case "transfer_window": {
      return {
        title: `${s.direction} transfers · ${s.date} · ${s.label} (${s.startTime}–${s.endTime})`,
        name: `${s.label} ${s.direction} transfers`,
      }
    }
  }
}

// ── display helpers ──────────────────────────────────────────────────────────

// A channel is "guest-facing" when attendees are members: every confirmed guest
// auto-joins, or membership follows an attendee group / a linked agenda item or
// group. Otherwise it's a staff-only (internal) channel. Drives the soft
// eggplant # badge (.ch-guest-ic).
export function isGuestFacing(c: Conversation): boolean {
  return (
    c.includesAllConfirmedGuests === 1 ||
    !!c.attendeeGroupId ||
    !!c.agendaItemId ||
    !!c.agendaGroupId ||
    (!!c.cohortKind && !!c.cohortKey)
  )
}

export function isArchived(c: Conversation): boolean {
  return !!c.archivedAt
}

// An internal channel scoped to an explicit staff roster (vs. all staff).
export function isSelectedStaff(c: Conversation): boolean {
  return !isGuestFacing(c) && c.staffAudience === "selected"
}

// Channel names are stored as slugs (e.g. "vip-lounge"); show "Vip Lounge".
export function properChannelName(name: string | null): string {
  if (!name || !name.trim()) return "Untitled channel"
  return name
    .replace(/[-_]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

export function relTime(iso: string | null): string {
  if (!iso) return ""
  const t = Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`)
  if (Number.isNaN(t)) return ""
  const m = Math.round((Date.now() - t) / 60000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}

export function formatExact(iso: string): string {
  const t = Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`)
  if (Number.isNaN(t)) return iso
  return new Date(t).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
